import { useState, useCallback } from "react";
import { useForm, type Control, type UseFormGetValues } from "react-hook-form";
import { toast } from "react-toastify";
import { useUpdateMapPlotRequest } from "@/features/map/hooks/useUpdateMapPlotRequest";
import type { PlotRequestTag } from "@/features/map/types";

/**
 * 地図プロットリクエストタグ編集用のフォームデータ型
 */
export interface MapPlotRequestTagFormData {
  mapPlotRequestTags: string[];
}

/**
 * 地図プロットリクエストの編集状態とロジックを管理するカスタムフック
 *
 * 識別名とタグの編集機能を提供します。
 */
export const usePlotRequestEditor = (
  plotRequestId: number | undefined,
  initialName: string,
  initialTags: PlotRequestTag[],
  onSuccess: () => void | Promise<void>
): {
  isEditingName: boolean;
  editedName: string;
  setEditedName: (name: string) => void;
  handleStartEditingName: () => void;
  handleCancelEditingName: () => void;
  handleNameSave: () => Promise<void>;
  isEditingMapPlotRequestTags: boolean;
  control: Control<MapPlotRequestTagFormData>;
  getValues: UseFormGetValues<MapPlotRequestTagFormData>;
  handleStartEditingMapPlotRequestTags: () => void;
  handleCancelEditingMapPlotRequestTags: () => void;
  handleMapPlotRequestTagsSave: () => Promise<void>;
} => {
  // 識別名の編集状態
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  // タグの編集状態
  const [isEditingMapPlotRequestTags, setIsEditingMapPlotRequestTags] =
    useState(false);

  // タグ編集用のフォーム
  const { control, getValues, reset } = useForm<MapPlotRequestTagFormData>({
    defaultValues: {
      mapPlotRequestTags: [],
    },
  });

  // 地図プロットリクエストの更新フック
  const { trigger: updatePlotRequest } = useUpdateMapPlotRequest(plotRequestId);

  /**
   * 識別名の編集モードを開始
   */
  const handleStartEditingName = useCallback((): void => {
    setEditedName(initialName);
    setIsEditingName(true);
  }, [initialName]);

  /**
   * 識別名の編集をキャンセル
   */
  const handleCancelEditingName = useCallback((): void => {
    setIsEditingName(false);
    setEditedName("");
  }, []);

  /**
   * 識別名を保存
   */
  const handleNameSave = useCallback(async (): Promise<void> => {
    if (!plotRequestId) return;

    try {
      await updatePlotRequest({
        name: editedName,
      });
      toast.success("識別名を更新しました");
      setIsEditingName(false);
      await onSuccess();
    } catch (error) {
      toast.error("識別名の更新に失敗しました");
    }
  }, [plotRequestId, editedName, updatePlotRequest, onSuccess]);

  /**
   * 地図プロットリクエストタグの編集モードを開始
   */
  const handleStartEditingMapPlotRequestTags = useCallback((): void => {
    reset({ mapPlotRequestTags: initialTags.map((tag) => tag.name) });
    setIsEditingMapPlotRequestTags(true);
  }, [initialTags, reset]);

  /**
   * 地図プロットリクエストタグの編集をキャンセル
   */
  const handleCancelEditingMapPlotRequestTags = useCallback((): void => {
    setIsEditingMapPlotRequestTags(false);
    reset({ mapPlotRequestTags: initialTags.map((tag) => tag.name) });
  }, [initialTags, reset]);

  /**
   * 地図プロットリクエストタグを保存
   */
  const handleMapPlotRequestTagsSave = useCallback(async (): Promise<void> => {
    if (!plotRequestId) return;

    const tagNames = getValues("mapPlotRequestTags");

    try {
      await updatePlotRequest({
        tags: tagNames,
      });
      toast.success("タグを更新しました");
      setIsEditingMapPlotRequestTags(false);
      await onSuccess();
    } catch (error) {
      toast.error("タグの更新に失敗しました");
    }
  }, [plotRequestId, getValues, updatePlotRequest, onSuccess]);

  return {
    // 識別名関連
    isEditingName,
    editedName,
    setEditedName,
    handleStartEditingName,
    handleCancelEditingName,
    handleNameSave,

    // タグ関連
    isEditingMapPlotRequestTags,
    control,
    getValues,
    handleStartEditingMapPlotRequestTags,
    handleCancelEditingMapPlotRequestTags,
    handleMapPlotRequestTagsSave,
  };
};
